import axios from "axios";
import {
  BASE_PROMPT,
  BASE_SYSTEM_PROMPT,
  buildUserContent,
  MessageContent,
  PdfExcerpt,
  RunPromptOptions,
  RunPromptResult,
} from "./aiCommon";
import { Reference } from "./reference";

let cachedOpenRouterApiKey: string | null = null;
let cachedOpenRouterApiKeyPromise: Promise<string> | null = null;
let cachedOpenRouterApiKeyFetchedAtMs = 0;
const OPENROUTER_API_KEY_TTL_MS = 55 * 60 * 1000; // backend issues ~1h keys; refresh a bit earlier

const OPENROUTER_CHAT_URL = "https://openrouter.ai/api/v1/chat/completions";
const OPENROUTER_MAX_ATTEMPTS = 5;
const OPENROUTER_BACKOFF_CAP_MS = 15000;
const RETRYABLE_HTTP_STATUSES = new Set([429, 500, 502, 503, 504]);
const RETRYABLE_AXIOS_CODES = new Set([
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_NETWORK",
]);

type AxiosLikeError = {
  code?: string;
  response?: {
    status?: number;
    data?: unknown;
    headers?: Record<string, unknown>;
  };
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object") {
    return value as Record<string, unknown>;
  }
  return null;
}

function isAxiosLikeError(err: unknown): err is AxiosLikeError {
  if (!err || typeof err !== "object") return false;
  const record = err as Record<string, unknown>;
  return (
    "response" in record ||
    "config" in record ||
    "request" in record ||
    typeof record.code === "string"
  );
}

function readErrorCode(body: unknown): number | null {
  const root = asRecord(body);
  const error = asRecord(root?.error);
  const code = error?.code;
  if (typeof code === "number" && Number.isFinite(code)) return code;
  if (typeof code === "string" && /^\d+$/.test(code)) return Number(code);
  return null;
}

function isRetryableOpenRouterError(err: unknown): boolean {
  if (!isAxiosLikeError(err)) {
    return false;
  }
  if (err.code && RETRYABLE_AXIOS_CODES.has(err.code)) {
    return true;
  }
  const status = err.response?.status;
  if (typeof status === "number" && RETRYABLE_HTTP_STATUSES.has(status)) {
    return true;
  }
  const bodyCode = readErrorCode(err.response?.data);
  if (bodyCode != null && RETRYABLE_HTTP_STATUSES.has(bodyCode)) {
    return true;
  }
  return false;
}

function getRetryDelayMs(err: unknown, attemptNum: number): number {
  if (isAxiosLikeError(err)) {
    const header = err.response?.headers?.["retry-after"];
    let raw: string | undefined;
    if (typeof header === "string") {
      raw = header;
    } else if (Array.isArray(header)) {
      const [first] = header;
      raw = first;
    }
    if (raw) {
      const asSeconds = Number(raw);
      if (Number.isFinite(asSeconds) && asSeconds >= 0) {
        return Math.min(asSeconds * 1000, OPENROUTER_BACKOFF_CAP_MS);
      }
    }
  }
  const exponential = 1000 * 2 ** (attemptNum - 1);
  return Math.min(exponential, OPENROUTER_BACKOFF_CAP_MS);
}

async function withOpenRouterRetry<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= OPENROUTER_MAX_ATTEMPTS; attempt += 1) {
    try {
      // Sequential attempts are required for backoff-based retries.
      // eslint-disable-next-line no-await-in-loop
      return await operation();
    } catch (err) {
      lastError = err;
      const canRetry =
        attempt < OPENROUTER_MAX_ATTEMPTS && isRetryableOpenRouterError(err);
      if (!canRetry) {
        throw err;
      }
      const delayMs = getRetryDelayMs(err, attempt);
      // eslint-disable-next-line no-console
      console.warn(
        `OpenRouter request failed (attempt ${attempt}/${OPENROUTER_MAX_ATTEMPTS}), retrying in ${delayMs}ms…`,
        err
      );
      // Wait before the next attempt so retries are spaced out.
      // eslint-disable-next-line no-await-in-loop
      await sleep(delayMs);
    }
  }
  throw lastError;
}

async function getOpenRouterApiKeyFromBackend(
  authToken?: string
): Promise<string> {
  if (
    cachedOpenRouterApiKey &&
    Date.now() - cachedOpenRouterApiKeyFetchedAtMs < OPENROUTER_API_KEY_TTL_MS
  ) {
    return cachedOpenRouterApiKey;
  }
  if (cachedOpenRouterApiKeyPromise) return cachedOpenRouterApiKeyPromise;

  cachedOpenRouterApiKeyPromise = (async () => {
    const host = process.env.HOST;
    if (!host)
      throw new Error("HOST env var is required to call backend GraphQL");

    const query = `
          query GetOpenRouterApiKey {
            GetOpenRouterApiKey
          }
        `;

    const { data } = await axios.post(
      `${host}/graphql`,
      { query },
      {
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        timeout: 15000,
      }
    );

    const apiKey = data?.data?.GetOpenRouterApiKey;
    if (!apiKey || typeof apiKey !== "string") {
      const message =
        data?.errors?.[0]?.message ||
        "Failed to obtain OpenRouter API key from backend";
      throw new Error(message);
    }

    cachedOpenRouterApiKey = apiKey;
    cachedOpenRouterApiKeyFetchedAtMs = Date.now();
    cachedOpenRouterApiKeyPromise = null;
    return apiKey;
  })().catch((err) => {
    cachedOpenRouterApiKeyPromise = null;
    throw err;
  });

  return cachedOpenRouterApiKeyPromise;
}

/**
 * Call an OpenRouter Chat model (default: openai/gpt-4o-mini) with a base prompt and PDF excerpts.
 * Obtains a short-lived OpenRouter API key from backend via GraphQL `GetOpenRouterApiKey`.
 */
export async function runPromptOpenRouter(
  sourceDocument: PdfExcerpt,
  reference: Reference,
  options: RunPromptOptions = {}
): Promise<RunPromptResult> {
  const apiKey = await getOpenRouterApiKeyFromBackend(options.authToken);
  // console.log("apiKey: ", apiKey);

  // const model = options.model || "openrouter/andromeda-alpha"; // słabo
  // const model = options.model || "openai/o3-deep-research"; // dobrze, ale drogo, 10USD za małą dokumentację testową
  // const model = options.model || "openai/gpt-5-mini"; // ok i tanio
  // const model = options.model || "openai/gpt-5"; // bardzo dobrze
  // const model = options.model || "openai/gpt-5.2"; //
  const model = options.model || "openai/gpt-5.6-sol"; //
  // const model = options.model || "mistralai/devstral-2512:free"; // słabo, źle formatuje HTML
  // const model = options.model || "allenai/olmo-3-32b-think:free"; //
  // const model = options.model || "openai/gpt-oss-120b:free"; //
  // const model = options.model || "google/gemma-3-27b-it:free"; // słabiutko
  // const model = options.model || "microsoft/phi-4"; // słabo
  // const model = options.model || "mistralai/mixtral-8x22b-instruct"; // tragedia
  // const model = options.model || "qwen/qwen3-235b-a22b-2507"; // jest potencjał
  // const model = options.model || "qwen/qwen3-vl-235b-a22b-instruct"; // jest potencjał, ale jeszcze średnio
  // const model = options.model || "google/gemini-2.5-pro"; // świetny
  // const model = options.model || "google/gemini-3.1-pro-preview";
  // const model = options.model || "google/gemini-2.5-flash"; // słabo, duzo bełkotu
  // const model = options.model || "z-ai/glm-4.6"; // bardzo spoko. krótkie i konkretne
  // const model = options.model || "x-ai/grok-4-fast"; // nieźle, ma problemy z formatowaniem HTML
  // const model = options.model || "x-ai/grok-4"; // dobrze, drogo, gorzej od gemini 2.5 pro i gpt-5
  // const model = options.model || "nvidia/nemotron-nano-9b-v2:free"; // zaskakująco dobry, do sprawdzenia
  // const model = options.model || "meta-llama/llama-4-maverick:free"; // słabo, nie radzi sobie z tworzeniem HTMLa
  // const model = options.model || "qwen/qwen3-max"; // całkiem nieźle
  // const model = options.model || "deepseek/deepseek-v3.1-terminus"; // nieźle, trochę się czepia
  const temperature =
    typeof options.temperature === "number" ? options.temperature : 1;
  const { maxTokens } = options;
  const systemMessage = options.system || BASE_SYSTEM_PROMPT;

  const basePrompt = options.basePrompt || BASE_PROMPT;

  const maxInputChars = options.maxInputChars || 160000; // soft guardrail (~160k chars)
  const { content: userContent, truncated } = buildUserContent(
    basePrompt,
    sourceDocument,
    reference,
    maxInputChars
  );

  const payload = {
    model,
    temperature,
    max_tokens: maxTokens,
    messages: [
      { role: "system", content: systemMessage },
      { role: "user", content: userContent },
    ],
  };

  const headers: Record<string, string> = {
    Authorization: `Bearer ${apiKey}`,
    "Content-Type": "application/json",
  };

  // Optional but recommended by OpenRouter for better routing/quotas
  try {
    if (typeof window !== "undefined") {
      headers["HTTP-Referer"] = window.location.origin;
      headers["X-Title"] = "PrzemyslawGierski";
    }
  } catch (_) {
    // ignore if window is not available
  }

  const { data } = await withOpenRouterRetry(() =>
    axios.post(OPENROUTER_CHAT_URL, payload, {
      headers,
      timeout: 720000 * 4,
    })
  );

  const choice = data?.choices?.[0];
  const text: string = choice?.message?.content || "";

  return {
    text,
    fileName: sourceDocument.fileName,
    model: data?.model || model,
    usage: data?.usage,
    reference,
    raw: data,
    ...(truncated ? { inputTruncated: true } : {}),
  };
}

export type ChatMessage = {
  role: "system" | "user" | "assistant";
  content: MessageContent;
};

export type ChatResult = {
  text: string;
  model: string;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  raw: unknown;
};

/**
 * Low-level OpenRouter chat call with arbitrary messages (no reference document).
 * Used e.g. for batched eCTD section classification of loose PDF files and for
 * vision transcription of scanned pages (`content` may be a content-part array).
 */
export async function runChatOpenRouter(
  messages: ChatMessage[],
  options: RunPromptOptions = {}
): Promise<ChatResult> {
  const apiKey = await getOpenRouterApiKeyFromBackend(options.authToken);
  const model = options.model || "openai/gpt-5.2";
  const temperature =
    typeof options.temperature === "number" ? options.temperature : 1;

  const headers: Record<string, string> = {
    Authorization: `Bearer ${apiKey}`,
    "Content-Type": "application/json",
  };
  try {
    if (typeof window !== "undefined") {
      headers["HTTP-Referer"] = window.location.origin;
      headers["X-Title"] = "PrzemyslawGierski";
    }
  } catch (_) {
    // ignore if window is not available
  }

  const { data } = await withOpenRouterRetry(() =>
    axios.post(
      OPENROUTER_CHAT_URL,
      {
        model,
        temperature,
        max_tokens: options.maxTokens,
        messages,
      },
      {
        headers,
        timeout: 720000,
      }
    )
  );

  return {
    text: data?.choices?.[0]?.message?.content || "",
    model: data?.model || model,
    usage: data?.usage,
    raw: data,
  };
}

export default runPromptOpenRouter;
